import { useState, type ReactNode } from 'react';
import type { DisplayStatus, LayoutStatus } from '../lib/types.js';
import { isOnline } from '../lib/format.js';

export function OnlineDot({ lastSeenAt }: { lastSeenAt: string | null }): ReactNode {
  const online = isOnline(lastSeenAt);
  return <span className={`dot ${online ? 'online' : 'offline'}`} title={online ? 'Online' : 'Offline'} />;
}

export function DisplayStatusBadge({ status }: { status: DisplayStatus }): ReactNode {
  const map: Record<DisplayStatus, string> = {
    pending: 'warn',
    active: 'good',
    disabled: 'bad',
  };
  return <span className={`badge ${map[status]}`}>{status}</span>;
}

export function LayoutStatusBadge({ status }: { status: LayoutStatus }): ReactNode {
  return <span className={`badge ${status === 'published' ? 'good' : 'flat'}`}>{status}</span>;
}

export function Loading({ what }: { what?: string }): ReactNode {
  return <div className="empty">Loading{what ? ` ${what}` : ''}…</div>;
}

export function ErrorNote({ error }: { error: Error }): ReactNode {
  return <div className="card card-pad form-error">{error.message}</div>;
}

export function EmptyState({ children }: { children: ReactNode }): ReactNode {
  return <div className="empty">{children}</div>;
}

export function PasswordInput({
  value,
  onChange,
  autoComplete,
  minLength,
  required,
  autoFocus,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  minLength?: number;
  required?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
}): ReactNode {
  const [visible, setVisible] = useState(false);
  return (
    <div className="password-input">
      <input
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        minLength={minLength}
        required={required}
        autoFocus={autoFocus}
        placeholder={placeholder}
      />
      <button
        type="button"
        className="password-toggle"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        tabIndex={-1}
      >
        {visible ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}
